import { PATIENT_READY_TOPIC } from '../../../utils/telemedicine';

import { useRoomContext } from '@livekit/components-react';
import { ConnectionState, RoomEvent } from 'livekit-client';
import { useEffect } from 'react';

const EMPTY_PAYLOAD = new Uint8Array(0);

/**
 * Tell the doctor's app that this client has finished connecting.
 *
 * The room reports a participant to everyone else the moment the SFU accepts them, which
 * happens while this client is still negotiating and still showing its loading state. The
 * doctor's app used that as "the patient joined", so its GUS consent alert could land over
 * a call that had not started yet. Nothing observable from the other side closes that gap:
 * the patient may join with camera and microphone off, so no track ever arrives.
 *
 * It re-announces whenever someone else joins and after a reconnect, because this is a
 * fire-and-forget message — a doctor who arrives late, or comes back from a blink, would
 * otherwise never hear it.
 */
const useAnnounceReady = () => {
	const room = useRoomContext();

	useEffect(() => {
		const announce = () => {
			if (room.state !== ConnectionState.Connected) {
				return;
			}

			room.localParticipant
				.publishData(EMPTY_PAYLOAD, { reliable: true, topic: PATIENT_READY_TOPIC })
				.catch(() => {});
		};

		announce();

		room.on(RoomEvent.Connected, announce);
		room.on(RoomEvent.Reconnected, announce);
		room.on(RoomEvent.ParticipantConnected, announce);

		return () => {
			room.off(RoomEvent.Connected, announce);
			room.off(RoomEvent.Reconnected, announce);
			room.off(RoomEvent.ParticipantConnected, announce);
		};
	}, [room]);
};

export default useAnnounceReady;
